import React, { useEffect } from "react";
import {
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  Grid,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import theme from "../theme";
import useButtonStyles from "../styles/useButtonStyles";
import { paymentOptions } from "../static-models/PaymentMode";
import { useAppSelector } from "../redux/hooks";
import axios from "axios";
import { APIConstants } from "../constants/ApiConstants";
import BasicModal from "../components/ui/BasicModal";
import Loader from "../components/ui/Loader";
import { usePermissionGetter } from "../Hooks/Permissions/userPermissionGetter";

interface Voucher {
  title: string;
  price: number;
  amount: number;
}
const paymentOptionsWithSerial = [
  "Voucher Extension (Old serial number)",
  "Voucher replacement (Old serial number)",
];

interface VoucherBillProps {
  reset: Function;
  vouchers: Voucher[];
  onVoucherCountChange: (voucherIndex: number, newCount: number) => void;
}

const VoucherBill: React.FC<VoucherBillProps> = ({
  reset,
  vouchers,
  onVoucherCountChange,
}) => {
  const buttonStyles = useButtonStyles();
  const { getUserPermission:getSalesPermission} = usePermissionGetter("sale_of_voucher", "sales");
  const [disable, setDisable] = React.useState<boolean>(true);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [paymentMode, setPaymentMode] = React.useState<string>("credit-card");
  const [serialNumber, setSerialNumber] = React.useState<string>("");
  const [remarks, setRemarks] = React.useState<string>("");
  const [showModal, setShowModal] = React.useState<boolean>(false);
  const [puchaseError, setPurchaseError] = React.useState<boolean>(false);
  const customerEmail = useAppSelector(
    (state: any) => state.persistReducers.getMemberList.membersList.email
  );

  const calculateTotalAmount = () => {
    const calculatedTotal = vouchers.reduce(
      (total, voucher) => total + voucher.price * voucher.amount,
      0
    );
    return calculatedTotal;
  };

  const handleVoucherCountChange = (index: number, newCount: number) => {
    onVoucherCountChange(index, newCount);
  };

  const resetForm = () => {
    reset();
    setPaymentMode("");
    setSerialNumber("");
    setRemarks("");
  };

  const purchaseEVoucher = async (payload: any) => {
    try {
      setLoading(true);
      const response = await axios.post(
        process.env.REACT_APP_API_URL + APIConstants.saleEvoucher,
        payload
      );
      setLoading(false);
      setPurchaseError(false);
    } catch (error) {
      setLoading(false);
      setPurchaseError(true);
      console.error("error", error);
    } finally {
      setShowModal(true);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const amount = calculateTotalAmount();
    const lineItems = vouchers.map((voucher: any) => {
      const totalAmount = voucher.price * voucher.amount;
      return {
        id: voucher.id,
        itemCode: voucher.title,
        amount: totalAmount,
        description: voucher.title,
        discount: 0,
        rate: voucher.price,
        qty: voucher.amount,
      };
    });
    const customFields = {
      payment_mode: paymentMode,
      serial_no: serialNumber,
      cs_email_id: localStorage?.username,
      remarks,
    };
    const payload = {
      lineItems,
      customFields,
      amount,
      customerEmail,
    };
    await purchaseEVoucher(payload);
    resetForm();
  };

  useEffect(() => {
    const totalAmount = calculateTotalAmount();
    if (totalAmount > 0) {
      setDisable(false);
    } else {
      setDisable(true);
    }
  }, [vouchers]);

  return (
    <Grid
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: theme.color?.background,
        fontFamily: theme.font?.fontFamily.primary,
        overflow:"auto",
      }}
      px={1}
    >
      <Typography
        variant="h5"
        style={{
          borderBottom: theme.border?.smallGrey,
          fontFamily: theme.font?.fontFamily.primary,
          fontWeight: theme.font?.fontWeight.thick,
          fontSize: theme.font?.fontSize.large,
        }}
        pb={2}
      >
        Pay Voucher
      </Typography>
      {getSalesPermission("create") && (
        <>
          <Grid
            border={theme.border?.newGreyBorder}
            borderRadius={"12px"}
            p={2}
            my={3}
          >
            {vouchers.map((voucher, index) => (
              <Grid
                key={index}
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <Typography
                  variant="body1"
                  style={{
                    fontFamily: theme.font?.fontFamily.primary,
                    fontWeight: "500",
                  }}
                >
                  {voucher.title} (${voucher.price.toFixed(2)}) X{" "}
                  {voucher.amount.toFixed(2)}
                </Typography>
                <Typography
                  style={{
                    fontFamily: theme.font?.fontFamily.primary,
                    fontWeight: "500",
                  }}
                >
                  ${(voucher.price * voucher.amount).toFixed(2)}
                </Typography>
              </Grid>
            ))}

            <Grid
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "2rem",
                fontFamily: theme.font?.fontFamily.primary,
              }}
            >
              <Typography
                variant="h6"
                style={{ color: theme.color?.secondary, fontWeight: "700" }}
              >
                Total
              </Typography>
              <Typography
                variant="h6"
                style={{ color: theme.color?.secondary, fontWeight: "700" }}
              >
                ${calculateTotalAmount().toFixed(4)}
              </Typography>
            </Grid>
          </Grid>
          <form onSubmit={handleSubmit}>
            <Typography
              style={{
                fontFamily: theme.font?.fontFamily.primary,
                fontWeight: "500",
                lineHeight: "1.5rem",
                marginBottom: "0.5rem",
              }}
            >
              Payment Mode *
            </Typography>
            <Select
              size="small"
              value={paymentMode}
              onChange={(event) => setPaymentMode(event.target.value as string)}
              style={{ width: "100%", marginBottom: "1rem" }}
            >
              {paymentOptions.map((option, index) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>

            {paymentOptionsWithSerial.some((opt) =>
              paymentMode.toLowerCase().includes(opt.toLowerCase())
            ) && (
              <>
                <Typography
                  style={{
                    fontFamily: theme.font?.fontFamily.primary,
                    fontWeight: "500",
                    lineHeight: "1.5rem",
                    marginBottom: "0.5rem",
                  }}
                >
                  Serial Number *
                </Typography>
                <TextField
                  size="small"
                  value={serialNumber}
                  onChange={(event) => setSerialNumber(event.target.value)}
                  style={{ width: "100%", marginBottom: "1rem" }}
                />
              </>
            )}

            <Typography
              style={{
                fontFamily: theme.font?.fontFamily.primary,
                fontWeight: "500",
                lineHeight: "1.5rem",
                marginBottom: "0.5rem",
              }}
            >
              Remarks *
            </Typography>
            <TextField
              size="small"
              value={remarks}
              onChange={(event) => setRemarks(event.target.value)}
              style={{ width: "100%", marginBottom: "1rem" }}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "10px",
              }}
            >
              <Button
                fullWidth
                sx={{
                  ...buttonStyles.containedSecondary,
                  background: disable
                    ? theme.color?.sideGrey
                    : theme.color?.secondary,
                  padding: "1rem",
                  fontWeight: "700",
                  borderRadius: "8px",
                  maxHeight: "2.8125",
                }}
                type="submit"
                disabled={
                  disable ||
                  remarks === "" ||
                  paymentMode === "" ||
                  (paymentOptionsWithSerial.some((opt) =>
                    paymentMode.toLowerCase().includes(opt.toLowerCase())
                  ) &&
                    serialNumber === "")
                }
              >
                <Grid
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    style={{
                      fontFamily: theme.font?.fontFamily.primary,
                      fontWeight: 700,
                    }}
                  >
                    ${calculateTotalAmount().toFixed(2)}
                  </Typography>
                  <Grid
                    container
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "right",
                    }}
                  >
                    <Typography
                      style={{
                        fontFamily: theme.font?.fontFamily.primary,
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      Settle Payment
                    </Typography>
                    <ArrowForwardIcon style={{ height: "1rem" }} />
                  </Grid>
                </Grid>
              </Button>
            </div>
          </form>
        </>
      )}
      {showModal &&
        (puchaseError ? (
          <BasicModal
            title={"Failed to process"}
            success={false}
            setState={setShowModal}
          />
        ) : (
          <BasicModal
            title={"Purchase Successful"}
            success={true}
            setState={setShowModal}
          />
        ))}
      {loading && <BasicModal content={<Loader />} />}
    </Grid>
  );
};

export default VoucherBill;
